import React from 'react';
import type { ExternalApiKey } from '../types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

/**
 * Card de API Key (view "Cards"). Padrão-lista: card flat/borda SEM checkbox
 * (F34 — seleção em massa só na tabela) + menu ••• de ações. Avatar sem foto =
 * slate #64748b com ícone de chave.
 *
 * Não há edição de API Key (segurança: chave completa nunca reexibida).
 */
export interface ApiKeyCardProps {
  apiKey: ExternalApiKey;
  onDelete?: (id: string) => void;
  onToggleStatus?: (id: string, currentStatus: string) => void;
}

export const ApiKeyCard: React.FC<ApiKeyCardProps> = ({
  apiKey,
  onDelete,
  onToggleStatus,
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const isActive = apiKey.status === 'active';

  const actions: CardAction[] = [];
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(apiKey.id, apiKey.status),
    });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(apiKey.id), variant: 'danger' });
  }

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`apikey-card-${apiKey.id}`}
    >
      {/* Topo: avatar + nome/descrição + ações */}
      <div className="flex items-start gap-3">
        <Avatar className="w-11 h-11 flex-shrink-0" isSquircle>
          <AvatarFallback isSquircle>
            <Icon name="key" className="w-5 h-5 text-white" />
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {apiKey.name}
          </h3>
          {apiKey.description && (
            <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
              {apiKey.description}
            </p>
          )}
        </div>

        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1">
            <CardActionsMenu actions={actions} size="sm" />
          </div>
        )}
      </div>

      {/* Chave mascarada */}
      <div className="mt-3">
        <code
          className="text-xs px-2 py-1 rounded font-mono block truncate"
          style={{
            backgroundColor: 'var(--theme-surface-sunken)',
            border: '1px solid var(--theme-card-border)',
            color: 'var(--theme-card-text)',
          }}
        >
          {apiKey.maskedKey}
        </code>
      </div>

      {/* Métricas: uso / último uso / expiração */}
      <div className="mt-3 flex flex-col gap-1.5">
        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--theme-card-text)' }}>
          <Icon name="activity" size={14} style={{ color: 'var(--theme-muted-text)' }} />
          <span className="truncate">
            <span className="tabular-nums font-medium">{apiKey.usageCount}</span> requisições · último uso {formatDate(apiKey.lastUsedAt)}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs" style={{ color: apiKey.expiresAt ? 'var(--theme-warning-color)' : 'var(--theme-muted-text)' }}>
          <Icon name="clock" size={14} style={{ color: 'currentColor' }} />
          <span className="truncate">Expira: {apiKey.expiresAt ? formatDate(apiKey.expiresAt) : 'Nunca'}</span>
        </div>
      </div>

      {/* Rodapé: status (F47/F62-65: StatusPill shared) */}
      <div className="mt-3 pt-3 flex items-center" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
        <StatusPill
          variant={isActive ? 'success' : 'neutral'}
          label={isActive ? 'Ativa' : 'Inativa'}
          className="ml-auto"
          data-testid={`apikey-card-status-${apiKey.id}`}
        />
      </div>
    </div>
  );
};

ApiKeyCard.displayName = 'ApiKeyCard';
